// BLD-28 /diagnosticos/:id/atribuicoes: the consultant chooses, per category, the workshop group and the participant
// who answers it (review V2 point 2). The group's manager validates; the consultant approves on the scenario page.
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, ErrorState, Panel, Pill, SelectField, Skeleton } from "../../components/ui.tsx";
import { GuardedLink } from "../../components/plan-shell.tsx";
import { useToast } from "../../components/toast.tsx";
import { ApiError, get, patch, put } from "../../lib/api.ts";
import {
  mBase, mKey, useWorkflow, type AssessmentDetail, type MaturityInstitution, type ScenarioDetail, type ScenarioKind, type WorkflowData,
} from "../../lib/maturity.ts";
import { AssessmentShell } from "./AssessmentShell.tsx";
import { WithInstitution } from "./MaturityLayout.tsx";
import { CATEGORY_STATE_LABEL, CATEGORY_STATE_TONE, OPEN_STATE } from "../../../shared/maturity/workflow.ts";
import type { DefCategory } from "../../../shared/maturity/definition.ts";

export function AssignmentsPage({ id }: { id: string }) {
  return (
    <WithInstitution>
      {(inst) => <AssessmentShell institution={inst} id={id} tab="atribuicoes" title="Atribuições">{(a) => <AssignmentsBody institution={inst} assessment={a} />}</AssessmentShell>}
    </WithInstitution>
  );
}

function useScenarioStates(inst: string, a: AssessmentDetail, kind: ScenarioKind) {
  return useQuery({
    queryKey: mKey(inst, "assessment", a.id, "scenario", kind),
    queryFn: async () => (await get<{ data: ScenarioDetail }>(`${mBase(inst)}/assessments/${a.id}/scenarios/${kind}`)).data,
    enabled: Boolean(a.scenarios[kind]),
  });
}

function AssignmentsBody({ institution, assessment: a }: { institution: MaturityInstitution; assessment: AssessmentDetail }) {
  const wf = useWorkflow(institution.id, a.id);
  const diagnosis = useScenarioStates(institution.id, a, "diagnosis");
  const prognosis = useScenarioStates(institution.id, a, "prognosis");
  if (wf.isPending) return <Skeleton lines={8} />;
  if (wf.isError) return <ErrorState error={wf.error} onRetry={() => void wf.refetch()} />;
  const data = wf.data;
  if (!data.plan) return <LinkPlan institution={institution} assessment={a} />;
  const closed = a.scenarios.diagnosis?.state === "finalized" && a.scenarios.prognosis?.state === "finalized";
  const noManager = data.groups.filter((g) => !g.manager_name);
  return (
    <div>
      <p className="mb-4 max-w-3xl text-sm text-muted">
        Escolha, para cada categoria, o grupo e o participante da oficina <strong className="text-ink">{data.plan.name}</strong> que vai responder.
        Ele verá a categoria na área “Diagnóstico” da oficina; o gestor do grupo valida e você aprova antes de encerrar.
        Categorias sem atribuição continuam sendo respondidas por você. O mesmo responsável vale para o prognóstico.
      </p>
      {!data.can_assign && <Alert tone="info">Somente instrutores da oficina vinculada podem alterar as atribuições.</Alert>}
      {data.groups.length === 0 && (
        <Alert tone="warn" title="A oficina ainda não tem grupos.">
          Crie os grupos e seus membros em <GuardedLink className="font-semibold underline" href={`/planos/${data.plan.id}/configuracoes/grupos`}>Configurações › Grupos</GuardedLink>.
        </Alert>
      )}
      {noManager.length > 0 && (
        <Alert tone="info" title={`Grupos sem gestor: ${noManager.map((g) => g.name).join(", ")}.`}>
          Sem gestor, as respostas enviadas vão direto para a sua aprovação. Defina o gestor em <GuardedLink className="font-semibold underline" href={`/planos/${data.plan.id}/configuracoes/grupos`}>Configurações › Grupos</GuardedLink>.
        </Alert>
      )}
      {a.definition.dimension.subdimensions.map((sd) => (
        <Panel key={sd.id} className="mb-4" as="section">
          <h2 className="text-[17px] font-semibold">{sd.title}</h2>
          <ul className="mt-3 divide-y divide-line">
            {sd.categories.map((c) => (
              <AssignmentRow key={`${c.id}:${data.assignments[c.id]?.version ?? 0}`} institution={institution} assessment={a} category={c} data={data}
                disabled={!data.can_assign || closed}
                states={[["Diag.", diagnosis.data?.categories?.[c.id]], ["Prog.", a.scenarios.prognosis ? prognosis.data?.categories?.[c.id] : undefined]]} />
            ))}
          </ul>
        </Panel>
      ))}
    </div>
  );
}

function AssignmentRow({ institution, assessment: a, category: c, data, disabled, states }: {
  institution: MaturityInstitution; assessment: AssessmentDetail; category: DefCategory; data: WorkflowData; disabled: boolean;
  states: [string, ScenarioDetail["categories"][string] | undefined][];
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const current = data.assignments[c.id];
  const [groupId, setGroupId] = useState(current?.group_id ?? "");
  const [memberId, setMemberId] = useState(current?.membership_id ?? "");
  const [busy, setBusy] = useState(false);
  const group = data.groups.find((g) => g.id === groupId);
  const changed = groupId !== (current?.group_id ?? "") || memberId !== (current?.membership_id ?? "");
  async function save() {
    setBusy(true);
    try {
      const res = await put<{ data: WorkflowData }>(`${mBase(institution.id)}/assessments/${a.id}/assignments/${c.id}`, {
        expected_version: current?.version ?? 0, group_id: groupId || null, membership_id: groupId ? memberId || null : null,
      });
      qc.setQueryData(mKey(institution.id, "assessment", a.id, "workflow"), res.data);
      toast("success", groupId ? "Categoria atribuída." : "Atribuição removida.");
    } catch (e) {
      toast("danger", e instanceof ApiError ? e.message : "Não foi possível salvar a atribuição.");
      if (e instanceof ApiError && e.code === "VERSION_CONFLICT") void qc.invalidateQueries({ queryKey: mKey(institution.id, "assessment", a.id, "workflow") });
    } finally {
      setBusy(false);
    }
  }
  return (
    <li className="grid gap-x-4 py-3 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)_auto] md:items-end">
      <div className="min-w-0 pb-2 md:pb-3">
        <p className="text-sm leading-snug font-semibold">{c.title}</p>
        <p className="mt-1 flex flex-wrap gap-1.5 text-xs text-muted">
          <span className="num">{c.questions.length} quesitos</span>
          {current && states.map(([label, st]) => st !== undefined || label === "Diag."
            ? <Pill key={label} tone={CATEGORY_STATE_TONE[(st ?? OPEN_STATE).state]}>{label} {CATEGORY_STATE_LABEL[(st ?? OPEN_STATE).state]}</Pill>
            : null)}
        </p>
      </div>
      <SelectField label="Grupo" value={groupId} disabled={disabled} onChange={(e) => { setGroupId(e.target.value); setMemberId(""); }}>
        <option value="">Sem atribuição (consultor)</option>
        {data.groups.map((g) => <option key={g.id} value={g.id}>{g.name}{g.manager_name ? "" : " (sem gestor)"}</option>)}
      </SelectField>
      <SelectField label="Quem responde" value={memberId} disabled={disabled || !group} onChange={(e) => setMemberId(e.target.value)}>
        <option value="">{group ? (group.members.length ? "Escolha um membro" : "Grupo sem membros") : "—"}</option>
        {group?.members.map((m) => <option key={m.membership_id} value={m.membership_id}>{m.name}{m.membership_id === group.manager_membership_id ? " (gestor)" : ""}</option>)}
      </SelectField>
      <div className="pb-3">
        <Button variant="secondary" disabled={disabled || !changed || (Boolean(groupId) && !memberId)} loading={busy} onClick={() => void save()}>Salvar</Button>
      </div>
    </li>
  );
}

function LinkPlan({ institution, assessment: a }: { institution: MaturityInstitution; assessment: AssessmentDetail }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [planId, setPlanId] = useState(institution.linkable_plans[0]?.id ?? "");
  const [busy, setBusy] = useState(false);
  async function link() {
    setBusy(true);
    try {
      await patch(`${mBase(institution.id)}/assessments/${a.id}`, { expected_version: a.version, linked_plan_id: planId });
      await qc.invalidateQueries({ queryKey: mKey(institution.id, "assessment", a.id) });
      toast("success", "Oficina vinculada.");
    } catch (e) {
      toast("danger", e instanceof ApiError ? e.message : "Não foi possível vincular a oficina.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <Panel className="max-w-2xl">
      <h2 className="text-lg font-bold">Vincule uma oficina para atribuir categorias</h2>
      <p className="mt-1 text-sm text-muted">Os participantes e grupos da oficina vinculada respondem e validam as categorias atribuídas.</p>
      {!a.capabilities.can_edit_identity ? (
        <Alert tone="info">O diagnóstico já foi encerrado; o vínculo não pode mais ser alterado. Duplique a avaliação para usar outra oficina.</Alert>
      ) : institution.linkable_plans.length === 0 ? (
        <Alert tone="info">Você não é instrutor de nenhuma oficina desta instituição.</Alert>
      ) : (
        <div className="mt-2 flex flex-wrap items-end gap-3">
          <SelectField label="Oficina" value={planId} onChange={(e) => setPlanId(e.target.value)}>
            {institution.linkable_plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </SelectField>
          <div className="pb-3"><Button loading={busy} disabled={!planId} onClick={() => void link()}>Vincular oficina</Button></div>
        </div>
      )}
    </Panel>
  );
}
